import React, { useContext } from 'react';
import styled from 'styled-components';
import { isBefore } from 'date-fns';
import { Divider, Text, Title, Icon } from 'components';
import { hasValidStripeMlsProductId } from 'utils';
import { AuthContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import {
  StyledBox,
  StyledIconWrapper,
  StyledPageContent,
} from '../StyledComponents';
import AppointmentDeskContext from '../AppointmentDeskContext';
import YourPlanInfo from '../Activate/YourPlanInfo';
import AnsweringServicePhone from './AnsweringServicePhone';
import ServiceLabels from '../ServiceLabels';
import {
  APPOINTMENT_DESK_LABELS,
  COMBINED_LIVE_CONCIERGE_LABELS,
  HOW_IT_WORKS_TEXT,
} from '../constants';

const Confirmation: React.FC = () => {
  const { answeringPortalConfig } = useContext(AuthContext);
  const { answeringSubscription, portalConfig, selectedStartDate } = useContext(
    AppointmentDeskContext,
  );
  const { sm } = useBreakpoint();

  const isMlsCovered = portalConfig?.appointmentDeskMlsPay;

  const datePassed = isBefore(
    new Date(answeringSubscription?.startDate || selectedStartDate),
    new Date(),
  );

  const currentPlan = answeringSubscription?.serviceType;

  return (
    <StyledPageContent>
      <StyledIconWrapper $width={64} $height={64}>
        <Icon type="antdLikeFilled" color="info" width={30} height={30} />
      </StyledIconWrapper>
      <StyledBox $my={3.6}>
        <Title level={2}>
          {datePassed
            ? 'Your concierge service is now live!'
            : 'Your subscription is confirmed!'}
        </Title>
      </StyledBox>
      <AnsweringServicePhone />
      <StyledConfirmationContainer $sm={sm}>
        <Title level={3}>How it works</Title>
        <StyledHowItWorksText type="secondary">
          {HOW_IT_WORKS_TEXT}
        </StyledHowItWorksText>
        <StyledBox $my={1}>
          <Divider />
        </StyledBox>
        <YourPlanInfo isConfirmPage isMlsCovered={isMlsCovered} />
        {datePassed &&
          hasValidStripeMlsProductId(answeringPortalConfig?.mlsProductId) && (
            <StyledServiceLabelsContainer>
              <StyledServiceLabelText>Included</StyledServiceLabelText>
              <ServiceLabels
                labels={[
                  ...(currentPlan === AnsweringServiceType.LiveConciergePlus
                    ? COMBINED_LIVE_CONCIERGE_LABELS
                    : APPOINTMENT_DESK_LABELS),
                ]}
              />
            </StyledServiceLabelsContainer>
          )}
      </StyledConfirmationContainer>
    </StyledPageContent>
  );
};

const StyledConfirmationContainer = styled.div<{ $sm: boolean }>`
  width: ${({ $sm }) => ($sm ? 'max(480px, 40%)' : '100%')};
  display: flex;
  flex-flow: column nowrap;
  margin-top: 24px;
`;

const StyledHowItWorksText = styled(Text)`
  font-size: 14px;
  line-height: 22px;
  margin-top: 4px;
`;

const StyledServiceLabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  padding: 24px;
`;

const StyledServiceLabelText = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
`;

export default Confirmation;
